import { OperationVariables } from '@apollo/client';
import { useEffect, useRef } from 'react';
import { useCustomLazyQuery } from '../../hooks/apollo/ApolloCustomHooks';
import { useCustomMessage } from '../../hooks/useCustomMessage';
import { ITenantSetting } from '../../interfaces/TenantSetting';
import { Query } from '../../services/graphql/query';

/** Only the latest paginated settings request may update the current tenant. */
export function useTenantSettingsRequest(
  tenantId: number | undefined,
  accountId: number | undefined,
) {
  const [fetchCount] = useCustomLazyQuery<{
    tenantSettingsCount: { count: number };
  }>(Query.tenantSettingsCount);
  const [fetchSettings] = useCustomLazyQuery<{
    tenantSettings: ITenantSetting[];
  }>(Query.tenantSettings);
  const { messageError } = useCustomMessage();
  const sequence = useRef(0);
  const active = useRef(false);
  const context = useRef({ tenantId, accountId });
  context.current = { tenantId, accountId };

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      sequence.current += 1;
    };
  }, [tenantId, accountId]);

  return async (variables: OperationVariables) => {
    if (tenantId == null || !active.current) return { success: false };
    const requestId = ++sequence.current;
    const isCurrent = () =>
      active.current &&
      requestId === sequence.current &&
      context.current.tenantId === tenantId &&
      context.current.accountId === accountId;
    try {
      const countVariables = { ...variables };
      delete countVariables.skip;
      delete countVariables.take;
      const [countResult, settingsResult] = await Promise.all([
        fetchCount({ variables: { ...countVariables, tenant_id: tenantId } }),
        fetchSettings({ variables: { ...variables, tenant_id: tenantId } }),
      ]);
      if (!isCurrent()) return { success: false };
      if (countResult.error) throw countResult.error;
      if (settingsResult.error) throw settingsResult.error;
      return {
        success: true,
        settings: settingsResult.data?.tenantSettings ?? [],
        total: countResult.data?.tenantSettingsCount.count ?? 0,
      };
    } catch (error) {
      if (isCurrent()) {
        messageError({ context: 'TenantSettingPage.request.1', error });
      }
      return { success: false };
    }
  };
}
